import { useCallback } from 'react';
import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { reorderColumns } from 'common/components/ConfigurableTable/utils/ColumnConfiguration.util';
import { cloneDeep } from 'lodash';
import { SimListResponseData } from 'src/common/components/Sim/Inventory/types/simListTypes';
import {
  ColumnConfiguration,
  ColumnWidth,
  TableConfiguration,
  TableVariant,
  TABLE_VARIANT,
} from '../constants/columnConfigurationsConstants';
import { useRootFontScale } from 'src/common/components/ConfigurableTable/Components/Base/useRootFontScale';

const updateColumnsVisibility = (
  columnsConfiguration: ColumnConfiguration[],
  columnName: string,
  enabled: boolean,
) => {
  const newColumnsConfiguration = [...columnsConfiguration];
  const indexOfColumn = newColumnsConfiguration.findIndex(({ name }) => {
    return name === columnName;
  });

  if (indexOfColumn < 0) {
    throw new Error('Column not found');
  }

  const columnConfiguration = newColumnsConfiguration[indexOfColumn];
  newColumnsConfiguration[indexOfColumn] = {
    ...columnConfiguration,
    enabled,
  };

  return newColumnsConfiguration;
};

const updateSelectedConfigurationColumnVisibility = (
  configurationName: 'combinedColumnConfigurations' | 'flatColumnConfigurations',
  currentConfiguration: TableConfiguration,
  columnName: string,
  enabled: boolean,
) => {
  try {
    const newColumnConfiguration = updateColumnsVisibility(
      currentConfiguration[configurationName],
      columnName,
      enabled,
    );

    return newColumnConfiguration;
  } catch {
    return currentConfiguration[configurationName];
  }
};

const updateColumnsWidth = (
  columnsConfiguration: ColumnConfiguration[],
  columnName: string,
  width: string,
  isCustomField: boolean,
) => {
  const newColumnsConfiguration: ColumnConfiguration[] = cloneDeep(columnsConfiguration);
  const indexOfColumn = newColumnsConfiguration.findIndex(({ name }) => {
    return name === columnName;
  });

  if (indexOfColumn < 0) {
    throw new Error('Column not found');
  }

  newColumnsConfiguration[indexOfColumn] = {
    ...newColumnsConfiguration[indexOfColumn],
    width,
    isCustomField,
  };

  return newColumnsConfiguration;
};

const updateSelectedConfigurationColumnWidth = (
  configurationName: 'combinedColumnConfigurations' | 'flatColumnConfigurations',
  currentConfiguration: TableConfiguration,
  columnName: string,
  width: string,
  isCustomField: boolean,
) => {
  try {
    const newColumnConfiguration = updateColumnsWidth(
      currentConfiguration[configurationName],
      columnName,
      width,
      isCustomField,
    );
    return newColumnConfiguration;
  } catch {
    console.log('failed');
    return currentConfiguration[configurationName];
  }
};

const tableVariantToConfigurationPropMap: Record<
  TableVariant,
  'combinedColumnConfigurations' | 'flatColumnConfigurations'
> = {
  0: 'combinedColumnConfigurations',
  1: 'flatColumnConfigurations',
} as const;

type useColumnConfigurationActionsProps = {
  configuration: TableConfiguration;
  queryKey: QueryKey;
  saveColumnWidth: (columnWidths: ColumnWidth, signal?: AbortSignal) => Promise<Response>;
  saveConfiguration: (
    config: { columnConfigurations: ColumnConfiguration[]; variant: TableVariant },
    signal?: AbortSignal,
  ) => Promise<Response>;
};

export const useColumnActions = ({
  configuration,
  queryKey,
  saveColumnWidth,
  saveConfiguration,
}: useColumnConfigurationActionsProps) => {
  const queryClient = useQueryClient();

  const fontScale = useRootFontScale();

  const updateLocallyColumnConfiguration = (
    variant: TableVariant,
    columnConfigurations: ColumnConfiguration[],
  ) => {
    queryClient.setQueriesData<SimListResponseData>(
      { queryKey },
      (simListResponseData: SimListResponseData | undefined): SimListResponseData => {
        if (!simListResponseData) {
          throw new Error('Configuration had to be fetched before!');
        }

        const { configuration, items } = simListResponseData;

        if (variant === TABLE_VARIANT.Flat) {
          return {
            items,
            configuration: {
              ...configuration,
              enabledVariant: variant,
              flatColumnConfigurations: columnConfigurations,
            },
          };
        } else {
          return {
            items,
            configuration: {
              ...configuration,
              combinedColumnConfigurations: columnConfigurations,
            },
          };
        }
      },
    );
  };

  const { mutate: mutateColumnConfigurtations } = useMutation({
    mutationFn: async ({
      variant,
      columnConfigurations,
    }: {
      variant: TableVariant;
      columnConfigurations: ColumnConfiguration[];
    }) => {
      saveConfiguration({
        variant: variant,
        columnConfigurations,
      });
    },
    onMutate: ({
      variant,
      columnConfigurations,
    }: {
      variant: TableVariant;
      columnConfigurations: ColumnConfiguration[];
    }) => {
      updateLocallyColumnConfiguration(variant, columnConfigurations);
    },
  });

  const { mutate: mutateWidth } = useMutation({
    mutationFn: (columnWidths: ColumnWidth) => saveColumnWidth(columnWidths),
  });

  const updateVisibiltyOfColumn = useCallback(
    (columnName: string, enabled: boolean) => {
      const configurationKey = tableVariantToConfigurationPropMap[configuration.enabledVariant];

      const columnConfigurations = updateSelectedConfigurationColumnVisibility(
        configurationKey,
        configuration,
        columnName,
        enabled,
      );

      mutateColumnConfigurtations({ variant: configuration.enabledVariant, columnConfigurations });
    },
    [configuration],
  );

  const updateVisibilityOfColumns = useCallback(
    (visiblityModel: Record<string, boolean>) => {
      const configurationKey = tableVariantToConfigurationPropMap[configuration.enabledVariant];

      const columnConfigurations = configuration[configurationKey].map((column) => ({
        ...column,
        enabled: visiblityModel[column.name],
      }));

      mutateColumnConfigurtations({ variant: configuration.enabledVariant, columnConfigurations });
    },
    [configuration],
  );

  const updateWidthOfColumn = useCallback(
    (columnName: string, width: 'auto' | number, isCustomField: boolean) => {
      const configurationKey = tableVariantToConfigurationPropMap[configuration.enabledVariant];

      const newWidth = width === 'auto' ? 'auto' : Math.floor(+width / fontScale).toString();

      const columnConfigurations = updateSelectedConfigurationColumnWidth(
        configurationKey,
        configuration,
        columnName,
        newWidth,
        isCustomField,
      );

      mutateWidth({ columnName, width: newWidth, isCustomField });

      updateLocallyColumnConfiguration(configuration.enabledVariant, columnConfigurations);
    },
    [configuration, fontScale],
  );

  const reorder = useCallback(
    (srcIndex: number, dstIndex: number) => {
      const configurationKey = tableVariantToConfigurationPropMap[configuration.enabledVariant];

      const columnConfigurations = reorderColumns(
        configuration[configurationKey],
        srcIndex,
        dstIndex,
      );

      mutateColumnConfigurtations({ variant: configuration.enabledVariant, columnConfigurations });
    },
    [configuration],
  );

  return {
    updateVisibiltyOfColumn,
    updateVisibilityOfColumns,
    updateWidthOfColumn,
    reorder,
  };
};
